import { makeAutoObservable, computed } from 'mobx';
import RootStore from 'stores/RootStore';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

class DashboardStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Today's production schedules
  get todaySchedules() {
    const today = new Date();
    const start = startOfDay(today).toISOString();
    const end = endOfDay(today).toISOString();

    const schedules = this.rootStore.productionSchedulesStore?.entities || [];
    return schedules.filter((schedule: any) => {
      const scheduleDate = new Date(schedule.scheduleDate);
      return scheduleDate >= new Date(start) && scheduleDate <= new Date(end);
    });
  }

  // This week's production schedules
  get weekSchedules() {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 1 }).toISOString();
    const end = endOfWeek(today, { weekStartsOn: 1 }).toISOString();

    const schedules = this.rootStore.productionSchedulesStore?.entities || [];
    return schedules.filter((schedule: any) => {
      const scheduleDate = new Date(schedule.scheduleDate);
      return scheduleDate >= new Date(start) && scheduleDate <= new Date(end);
    });
  }

  // This month's production schedules
  get monthSchedules() {
    const today = new Date();
    const start = startOfMonth(today).toISOString();
    const end = endOfMonth(today).toISOString();

    const schedules = this.rootStore.productionSchedulesStore?.entities || [];
    return schedules.filter((schedule: any) => {
      const scheduleDate = new Date(schedule.scheduleDate);
      return scheduleDate >= new Date(start) && scheduleDate <= new Date(end);
    });
  }

  // Today's loads/shipments
  get todayLoads() {
    const today = new Date();
    const start = startOfDay(today).toISOString();
    const end = endOfDay(today).toISOString();

    const loads = this.rootStore.loadStore?.entities || [];
    return loads.filter((load: any) => {
      const loadDate = new Date(load.scheduledDate || load.createdAt);
      return loadDate >= new Date(start) && loadDate <= new Date(end);
    });
  }

  // Get products for production schedules
  get products() {
    return this.rootStore.manufacturingProductsStore?.entities || [];
  }

  // Get manhole components (inventory items)
  get manholeComponents() {
    return this.rootStore.manholeComponentsStore?.entities || [];
  }

  // Get inventory items
  get inventoryItems() {
    return this.rootStore.inventoryStore?.entities || [];
  }

  // Get BOM components
  get bomComponents() {
    return this.rootStore.manufacturingBomComponentsStore?.entities || [];
  }

  // Calculate total quantity for today
  get todayTotalQuantity() {
    return this.todaySchedules.reduce((sum: number, schedule: any) => {
      return sum + (schedule.quantity || 0);
    }, 0);
  }

  // Calculate total quantity for the week
  get weekTotalQuantity() {
    return this.weekSchedules.reduce((sum: number, schedule: any) => {
      return sum + (schedule.quantity || 0);
    }, 0);
  }

  // Calculate total quantity for the month
  get monthTotalQuantity() {
    return this.monthSchedules.reduce((sum: number, schedule: any) => {
      return sum + (schedule.quantity || 0);
    }, 0);
  }

  // Group today's schedules by product
  get todayByProduct() {
    const grouped = new Map<string, { productId: string; productName: string; quantity: number }>();

    this.todaySchedules.forEach((schedule: any) => {
      const productId = schedule.productId;
      const product = this.products.find((p: any) => p.id === productId);
      const productName = product?.name || 'Unknown Product';

      if (grouped.has(productId)) {
        const existing = grouped.get(productId)!;
        existing.quantity += schedule.quantity || 0;
      } else {
        grouped.set(productId, {
          productId,
          productName,
          quantity: schedule.quantity || 0
        });
      }
    });

    return Array.from(grouped.values());
  }

  // Group week's schedules by product
  get weekByProduct() {
    const grouped = new Map<string, { productId: string; productName: string; quantity: number }>();

    this.weekSchedules.forEach((schedule: any) => {
      const productId = schedule.productId;
      const product = this.products.find((p: any) => p.id === productId);
      const productName = product?.name || 'Unknown Product';

      if (grouped.has(productId)) {
        const existing = grouped.get(productId)!;
        existing.quantity += schedule.quantity || 0;
      } else {
        grouped.set(productId, {
          productId,
          productName,
          quantity: schedule.quantity || 0
        });
      }
    });

    return Array.from(grouped.values());
  }

  // Group week's schedules by day
  get weekByDay() {
    const today = new Date();
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const data = days.map(() => new Map<string, number>());

    this.weekSchedules.forEach((schedule: any) => {
      const scheduleDate = new Date(schedule.scheduleDate);
      const dayOfWeek = (scheduleDate.getDay() + 6) % 7; // Convert to Monday = 0
      const productId = schedule.productId;
      const product = this.products.find((p: any) => p.id === productId);
      const productName = product?.name || 'Unknown';

      const dayData = data[dayOfWeek];
      dayData.set(productName, (dayData.get(productName) || 0) + (schedule.quantity || 0));
    });

    return { days, data };
  }

  // Get low stock items
  get lowStockItems() {
    const inventory = this.inventoryItems;
    return inventory.filter((item: any) => {
      const qty = item.quantity || 0;
      const min = item.minQuantity || 0;
      return qty <= min && min > 0;
    });
  }

  // Get active alerts (low stock, pending loads, etc.)
  get activeAlerts() {
    const alerts: Array<{id: string; message: string; severity: string; link?: string}> = [];

    // Low stock alerts
    this.lowStockItems.forEach((item: any, index: number) => {
      alerts.push({
        id: `stock-${index}`,
        message: `Low stock: ${item.name || 'Unknown item'} (${item.quantity || 0} units)`,
        severity: 'warning'
      });
    });

    // Pending production schedules
    const pendingSchedules = this.todaySchedules.filter((s: any) =>
      s.status === 'planned' || s.status === 'pending'
    );
    if (pendingSchedules.length > 0) {
      alerts.push({
        id: 'pending-schedules',
        message: `${pendingSchedules.length} production schedule(s) pending for today`,
        severity: 'info'
      });
    }

    // In progress loads
    const inProgressLoads = (this.rootStore.loadStore?.entities || []).filter((load: any) =>
      load.status === 'loading' || load.status === 'in_transit'
    );
    if (inProgressLoads.length > 0) {
      alerts.push({
        id: 'in-progress-loads',
        message: `${inProgressLoads.length} load(s) currently in transit`,
        severity: 'info'
      });
    }

    return alerts;
  }

  // Monthly metrics for radar chart
  get monthlyMetrics() {
    const schedules = this.monthSchedules;
    const totalScheduled = schedules.length;
    const completedSchedules = schedules.filter((s: any) => s.status === 'completed').length;
    const inProgressSchedules = schedules.filter((s: any) => s.status === 'in_progress').length;

    // Calculate metrics (as percentages)
    const completionRate = totalScheduled > 0 ? (completedSchedules / totalScheduled) * 100 : 0;
    const utilizationRate = totalScheduled > 0 ? ((completedSchedules + inProgressSchedules) / totalScheduled) * 100 : 0;
    const onTimeRate = totalScheduled > 0 ? Math.min(100, (completedSchedules / totalScheduled) * 110) : 0; // Estimated

    // Quality and safety would come from QC store if available
    const qualityScore = 90; // Placeholder
    const safetyScore = 95; // Placeholder

    return {
      efficiency: completionRate,
      quality: qualityScore,
      safety: safetyScore,
      onTimeDelivery: onTimeRate,
      clientSatisfaction: 85, // Placeholder
      capacityUsage: utilizationRate
    };
  }

  // Monthly summary by product
  get monthByProduct() {
    const grouped = new Map<string, { productId: string; productName: string; quantity: number; completed: number }>();

    this.monthSchedules.forEach((schedule: any) => {
      const productId = schedule.productId;
      const product = this.products.find((p: any) => p.id === productId);
      const productName = product?.name || 'Unknown Product';
      const isCompleted = schedule.status === 'completed';

      if (grouped.has(productId)) {
        const existing = grouped.get(productId)!;
        existing.quantity += schedule.quantity || 0;
        if (isCompleted) {
          existing.completed += schedule.quantity || 0;
        }
      } else {
        grouped.set(productId, {
          productId,
          productName,
          quantity: schedule.quantity || 0,
          completed: isCompleted ? (schedule.quantity || 0) : 0
        });
      }
    });

    return Array.from(grouped.values());
  }
}

export default DashboardStore;
